import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert2';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Button,
    Box
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import AutoAwesomeIcon from '@material-ui/icons/EmojiObjectsOutlined';

import requestLinkedService from '../../../domain/services/request/linked/request.linked.service';
import communicationProvider from '../../../providers/communication.provider';
import LoadingProgress from '../../LoadingProgress';
import { duplicateOf, resolveDuplicate } from './linked.duplicate.dialog';

const BRAND_BLUE = '#366FD0';

// pieceNote vem do agente: vai escapado para o HTML do modal.
const escapeHtml = (value = '') =>
    String(value).replace(
        /[&<>"']/g,
        (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
    );

const formatPrice = (price) =>
    Number(price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Peças que a equipe já deixou prontas a partir desta solicitação. Cada uma vira uma solicitação
// própria (com ID próprio) quando o cliente confirma — e só aí o crédito é descontado.
const SuggestedRequestsAccordion = ({ requestId, onChange, defaultExpanded = true }) => {
    const history = useHistory();
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [expanded, setExpanded] = useState(defaultExpanded);
    const mounted = useRef(true);

    useEffect(() => () => (mounted.current = false), []);

    useEffect(() => {
        if (requestId) load();
    }, [requestId]);

    const load = async () => {
        setLoading(true);
        const values = await requestLinkedService
            .findSuggestions(requestId)
            .catch(() => [])
            .finally(() => mounted.current && setLoading(false));
        if (mounted.current) setSuggestions(values);
    };

    const goToRegistration = (suggestion) => history.push(`/solicitations/new/${suggestion.id}`);

    const confirmationText = (suggestion) => {
        if (!suggestion.hasBalance)
            return suggestion.price
                ? `Esta peça custa ${formatPrice(
                      suggestion.price
                  )} e o seu saldo atual não cobre. Ao confirmar, a solicitação fica aguardando a compra de saldo.`
                : 'O seu saldo atual não cobre esta peça. Ao confirmar, a solicitação fica aguardando a compra de saldo.';
        return `Ao confirmar, ${formatPrice(
            suggestion.price
        )} serão descontados do seu pacote e a peça entra em produção em uma nova solicitação.`;
    };

    const handleConfirm = async (suggestion, duplicateReason = null) => {
        if (!suggestion.readyToConfirm) return goToRegistration(suggestion);

        const { isConfirmed } = duplicateReason
            ? { isConfirmed: true }
            : await swal.fire({
                  titleText: `Confirmar ${suggestion.pieceNote}?`,
                  text: confirmationText(suggestion),
                  icon: 'question',
                  showCancelButton: true,
                  confirmButtonText: 'Confirmar',
                  cancelButtonText: 'Voltar',
                  reverseButtons: true,
                  confirmButtonColor: BRAND_BLUE
              });
        if (!isConfirmed) return;

        setProcessingId(suggestion.id);
        await requestLinkedService
            .confirmSuggestion(
                requestId,
                suggestion.id,
                duplicateReason ? { allowDuplicate: true, duplicateReason } : {}
            )
            .then(async (result) => {
                const { isConfirmed: follow } = await swal.fire({
                    title: `Solicitação #${result.requestId} criada`,
                    html: result.isFinancialPending
                        ? 'A peça foi registrada e aguarda a compra de saldo para entrar em produção.'
                        : `A peça <b>${escapeHtml(
                              suggestion.pieceNote
                          )}</b> já entrou em produção. Acompanhe pela solicitação <b>#${
                              result.requestId
                          }</b>.`,
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonText: 'Acompanhar a nova solicitação',
                    cancelButtonText: 'Ficar aqui',
                    reverseButtons: true,
                    confirmButtonColor: BRAND_BLUE
                });
                // Navegação completa: a página da solicitação só carrega os dados na montagem, e
                // um push para outro ID manteria a origem na tela. Recarregar a lista só ao ficar aqui.
                if (follow) return window.location.assign(`/solicitations/${result.requestId}`);
                await load();
                onChange && onChange();
            })
            .catch(async (err) => {
                // Cadastro incompleto: leva o cliente ao formulário da própria peça sugerida.
                if (err?.response?.data?.fieldWithError) return goToRegistration(suggestion);
                const duplicate = duplicateOf(err);
                if (duplicate) {
                    // A mesma peça já foi pedida por outro caminho: nada é cobrado sem o cliente decidir.
                    const choice = await resolveDuplicate(duplicate);
                    if (choice.action === 'open')
                        return window.location.assign(`/solicitations/${choice.requestId}`);
                    if (choice.action === 'retry') {
                        if (mounted.current) setProcessingId(null);
                        return handleConfirm(suggestion, choice.duplicateReason);
                    }
                    return;
                }
                communicationProvider.getErrorMessage(
                    err,
                    null,
                    'Não foi possível confirmar a peça sugerida.'
                );
            })
            .finally(() => mounted.current && setProcessingId(null));
    };

    const handleDecline = async (suggestion) => {
        const { isConfirmed } = await swal.fire({
            title: 'Dispensar esta sugestão?',
            text: 'Nada será cobrado e a sugestão deixa de aparecer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Dispensar',
            cancelButtonText: 'Voltar',
            reverseButtons: true,
            confirmButtonColor: BRAND_BLUE
        });
        if (!isConfirmed) return;

        setProcessingId(suggestion.id);
        await requestLinkedService
            .declineSuggestion(requestId, suggestion.id)
            .then(load)
            .catch((err) =>
                communicationProvider.getErrorMessage(
                    err,
                    null,
                    'Não foi possível dispensar a sugestão.'
                )
            )
            .finally(() => mounted.current && setProcessingId(null));
    };

    if (!isLoading && suggestions.length === 0) return null;

    return (
        <div className="mb-4" data-testid="suggested-requests">
            {isLoading && <LoadingProgress transparentBackground={true} />}

            {!isLoading && suggestions.length > 0 && (
                <Accordion
                    expanded={expanded}
                    onChange={(event, isExpanded) => setExpanded(isExpanded)}
                    className="!rounded-lg !border !border-[#93c5fd] overflow-hidden before:!hidden"
                    style={{ margin: expanded ? '0 0 16px 0' : undefined }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon className="text-blue-700" />}
                        className="!bg-[#f0f7ff] !rounded-lg"
                        style={{ borderBottom: expanded ? '1px solid #93c5fd' : 'none' }}
                        classes={{ content: 'flex items-center !my-3' }}
                    >
                        <Box className="ml-2 mr-5 bg-[#2563eb] rounded-full flex items-center justify-center w-10 h-10 flex-shrink-0">
                            <AutoAwesomeIcon className="text-white" style={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography className="!text-lg !font-medium" variant="subtitle1">
                                {suggestions.length === 1
                                    ? 'Deixamos uma nova peça pronta para você'
                                    : 'Deixamos novas peças prontas para você'}
                            </Typography>
                            <Typography
                                className="!text-sm !text-gray-500"
                                variant="caption"
                                color="textSecondary"
                            >
                                Basta confirmar: cada peça vira uma nova solicitação, com
                                acompanhamento próprio.
                            </Typography>
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails className="flex flex-col !p-6">
                        {suggestions.map((suggestion, index) => (
                            <Box
                                key={suggestion.id}
                                className={`flex flex-col p-4 border border-[#ededed] rounded-lg ${
                                    index === suggestions.length - 1 ? 'mb-0' : 'mb-4'
                                } md:flex-row md:items-center md:justify-between`}
                            >
                                <Box className="flex-1 mb-3 md:mb-0 md:mr-4">
                                    <div className="flex items-center gap-2">
                                        <DescriptionOutlinedIcon className="h-5 w-5 text-blue-600" />
                                        <Typography
                                            className="!text-base !font-medium"
                                            variant="body1"
                                        >
                                            {suggestion.pieceNote}
                                        </Typography>
                                    </div>
                                    <Typography
                                        className="!text-sm !text-gray-500 !mt-1"
                                        variant="body2"
                                    >
                                        {suggestion.serviceName} · {suggestion.modality} ·{' '}
                                        {suggestion.area}
                                        {suggestion.price
                                            ? ` · ${formatPrice(suggestion.price)}`
                                            : ''}
                                    </Typography>
                                    {suggestion.reason && (
                                        <Typography
                                            className="!text-sm !text-gray-700 !mt-2"
                                            variant="body2"
                                        >
                                            {suggestion.reason}
                                        </Typography>
                                    )}
                                </Box>

                                <Box className="flex flex-row gap-2 items-center">
                                    <Button
                                        className="!rounded-lg !text-sm h-10 !font-normal"
                                        variant="text"
                                        size="small"
                                        disabled={processingId === suggestion.id}
                                        onClick={() => handleDecline(suggestion)}
                                    >
                                        Dispensar
                                    </Button>
                                    <Button
                                        className="!rounded-lg !text-sm md:!text-base h-10 !font-normal"
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        disabled={processingId === suggestion.id}
                                        onClick={() => handleConfirm(suggestion)}
                                    >
                                        {suggestion.readyToConfirm
                                            ? 'Confirmar'
                                            : 'Revisar e confirmar'}
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>
            )}
        </div>
    );
};

export default SuggestedRequestsAccordion;
